/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Input, OnInit, signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import * as _ from 'lodash';

import template from './similar-keyword-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';
import {deferSignalStateWrite} from '../../common/util/template-state';
import {TargetingSearchApiService} from '../targeting-search-api.service';

export const LANGUAGES = [
  {code: 'en', name: 'English'},
  {code: 'no', name: 'Norsk'},
  {code: 'sv', name: 'Svenska'},
  {code: 'da', name: 'Dansk'}
];

export const DEFAULT_MATCH_TYPE = {match: 'BROAD', threshold: 0.43, name: 'Broad'};

export const MATCH_TYPES = [
  {match: 'VERY_NARROW', threshold: 0.7, name: 'Very Narrow'},
  {match: 'NARROW', threshold: 0.65, name: 'Narrow'},
  DEFAULT_MATCH_TYPE,
  {match: 'VERY_BROAD', threshold: 0.3, name: 'Very Broad'}
];

import {TARGETING_IMPORTS} from '../targeting-imports';
import {TargetingTemplateComponent} from './targeting-template.component';

@Component({
  imports: [...TARGETING_IMPORTS, TargetingTemplateComponent],
  selector: 'adn-similar-keyword-targeting',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SimilarKeywordTargetingComponent implements OnInit {
  @Input() model: any[];
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.similarKeyword;
  @Input() underModel: any;
  @Input() runningModel: any;
  @Input() modelLineItem: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  loaded = true;
  readonly languages = LANGUAGES;
  readonly matchTypes = MATCH_TYPES;
  private readonly listState = signal<any>({});
  get list(): any { return this.listState(); }
  set list(value: any) { this.listState.set(value); }
  resultsIndex: number;
  private readonly embeddingState = signal(false);
  get embedding(): boolean { return this.embeddingState(); }
  set embedding(value: boolean) { this.embeddingState.set(value); }
  /** Keep object identity + keyword:'' — replacing searchMeta without keyword is NG0100. */
  private readonly searchMetaState = signal<any>({
    language: 'en',
    threshold: DEFAULT_MATCH_TYPE.threshold,
    resolvedError: '',
    resolved: true,
    keyword: ''
  }, {equal: () => false});
  get searchMeta(): any { return this.searchMetaState(); }
  set searchMeta(value: any) { this.searchMetaState.set(value); }

  private isPreparingForSave: any;

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      const model = this.model || [];
      const summary = _.map(_.filter(model, (entry) => _.get(entry, ['keyword', 'word'])), (entry) => {
        return (entry.negated ? 'NOT ' : '') + entry.keyword.word;
      }).join(', ');
      return {
        vm: _.cloneDeep(this.searchMeta),
        model: _.cloneDeep(model),
        constant: ADN_TARGETING_TYPES.similarKeyword,
        summary
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(private readonly targetingSearchApi: TargetingSearchApiService) {
  }

  ngOnInit(): void {
    this.ensureModel();
    this.resetSearchAndInputFields();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.similarKeyword.widget, () => {
      Promise.resolve(this.isPreparingForSave).then(() => {
        if (this.model.length === 0) {
          this.model.push({});
        }
        this.searchMeta = this.searchMeta;
      });
    }, copyTemplate);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
  }

  addNew(): void {
    this.model.push({});
  }

  editTarget(target: any): void {
    this.resetSearchAndInputFields();
    _.forEach(this.model, (t) => {
      if (_.isEqual(target, t)) {
        target.editable = !target.editable;
      } else {
        t.editable = false;
      }
    });
    if (target.editable) {
      const num = _.findIndex(this.model, (t) => !_.get(t, ['keyword', 'word']));
      if (num > -1) {
        _.pullAt(this.model, num);
      }
      this.searchMeta.keyword = target.keyword.word;

      const matchType = _.find(MATCH_TYPES, (mt) => mt.match === target.match);
      this.searchMeta.threshold = matchType ? matchType.threshold : DEFAULT_MATCH_TYPE.threshold;
      this.searchMeta.language = target.language;
    }
  }

  removeTarget(index: number): void {
    _.pullAt(this.model, index);

    if (this.model.length === 0) {
      this.model.push({});
    }
    this.resetSearchAndInputFields();
  }

  cancelResults(): void {
    this.list = {};
  }

  addToTargeting(negated: boolean, index: number): any {
    if (!_.isString(this.searchMeta.keyword) || _.trim(this.searchMeta.keyword).length < 2 ||
      !this.searchMeta.threshold || !this.searchMeta.language) {
      return Promise.resolve();
    }
    this.embedding = true;
    return firstValueFrom(this.targetingSearchApi.searchSimilarKeywords({
      targetWord: this.searchMeta.keyword,
      language: this.searchMeta.language,
      threshold: this.searchMeta.threshold
    })).then((page) => {
      const matchedType = _.find(MATCH_TYPES, (mt) => mt.threshold === this.searchMeta.threshold);
      const modelEntry = {
        match: matchedType ? matchedType.match : DEFAULT_MATCH_TYPE.match,
        negated: !!negated,
        keyword: page.word,
        language: this.searchMeta.language
      };
      if (this.model.length === 0) {
        this.model.push(modelEntry);
      } else {
        this.model[index] = modelEntry;
      }
      this.embedding = false;
      // Macrotask — clearing keyword mid-CD after add is NG0100 ('word'→undefined/'').
      deferSignalStateWrite(() => this.resetSearch());
    });
  }

  search(kw: any, index?: number): void {
    this.resultsIndex = _.isFinite(kw) ? kw : index;

    const searchParams: any = {
      targetWord: _.get(kw, ['keyword', 'word'], this.searchMeta.keyword || ''),
      language: _.get(kw, ['language'], this.searchMeta.language),
      threshold: this.searchMeta.threshold,
      all: true
    };

    this.searchMeta.resolved = false;
    searchParams.page = this.searchMeta.page;

    const runningModel = _.cloneDeep(this.runningModel);
    delete runningModel[ADN_TARGETING_TYPES.similarKeyword.targets];

    this.searchMeta.count = 0;

    this.targetingSearchApi.searchSimilarKeywords(searchParams).subscribe((page) => {
      _.forEach(page.matches, (match: any) => {
        const matchingType = _.find(MATCH_TYPES, (mt) => mt.threshold <= match.similarity);
        match.matching = matchingType ? matchingType.name : undefined;
      });
      const list = {results: page.matches, totalHits: page.totalWords};
      const resultCount = list.results.length;
      this.list = list;
      this.searchMeta.resultCount = resultCount;
      this.searchMeta.resolved = true;
    });
  }

  onSearchKeypress(event: KeyboardEvent, index: number): void {
    if (event && event.key === 'Enter') {
      event.preventDefault();
      this.search(index);
    }
  }

  onSearchButtonClick(event: Event, index: number): void {
    if (event && _.isFunction((event as any).stopPropagation)) {
      (event as any).stopPropagation();
    }
    this.search(index);
  }

  private ensureModel(): void {
    if (!_.isArray(this.model)) {
      return;
    }
  }

  private resetSearch(): void {
    // Mutate in place and always set keyword to '' (not omit/undefined) so the
    // [(ngModel)] input does not see 'tørt hår'→undefined during verify (NG0100).
    this.searchMeta.language = 'en';
    this.searchMeta.threshold = DEFAULT_MATCH_TYPE.threshold;
    this.searchMeta.resolvedError = '';
    this.searchMeta.resolved = true;
    this.searchMeta.keyword = '';
    this.searchMeta.error = '';
    this.searchMeta.resultCount = 0;
    this.searchMeta.count = 0;
    this.searchMeta = this.searchMeta;
  }

  private resetSearchAndInputFields(): void {
    this.resetSearch();
    this.list = {};
  }

  private prepareForSave(): any {
    const num = _.findIndex(this.model, (kw) => kw.editable);
    this.isPreparingForSave = Promise.resolve(this.addToTargeting(false, num >= 0 ? num : (this.model.length - 1))).then(() => {
      const indicesForRemoval = _.filter(_.map(this.model, (kw, index) => {
        return _.get(kw, ['keyword', 'word']) ? -1 : index;
      }), (n) => n > -1);
      _.pullAt(this.model, indicesForRemoval);
    });
    return this.isPreparingForSave;
  }

  private copyTemplate(tpl: any): void {
    if (!_.isArray(this.model)) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.model.length = 0;
    Array.prototype.push.apply(this.model, copied);
    if (this.model.length === 0) {
      this.model.push({});
    }
    _.forEach(this.model, (t) => {
      delete t.editable;
    });
    this.resetSearchAndInputFields();
  }
}
